import { useEffect, useRef } from 'react';
import { workspaceRevisionsAreCurrent } from '../../shared/ux/workspace-projection.ts';

export const PROCESSING_POLL_MS = 2500;
export const REVISION_POLL_MS = 5000;

export function shouldPollWorkspace(processingState: string | null | undefined): boolean {
  return Boolean(processingState && processingState !== 'IDLE');
}

export function shouldRefetchFromRevisions(
  cached: readonly { transactionId: string; factsRevision?: number }[],
  live: readonly { transactionId: string; factsRevision?: number }[],
): boolean {
  if (!cached.length && !live.length) return false;
  return !workspaceRevisionsAreCurrent(cached, live);
}

export function usePortalWorkspaceRefresh(
  load: () => void | Promise<void>,
  options: {
    processingState?: string | null;
    revisions?: readonly { transactionId: string; factsRevision?: number }[];
    transactionIds?: readonly string[];
  } = {},
): void {
  const revisionsKey = (options.revisions ?? []).map((row) => `${row.transactionId}:${row.factsRevision ?? 0}`).join('|');
  const idsKey = (options.transactionIds ?? []).join('|');
  const lastLive = useRef<string>('');

  useEffect(() => {
    void load();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    const onFocus = () => { void load(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onFocus);
    };
  }, [load]);

  useEffect(() => {
    if (!shouldPollWorkspace(options.processingState)) return undefined;
    const timer = window.setInterval(() => { void load(); }, PROCESSING_POLL_MS);
    return () => window.clearInterval(timer);
  }, [load, options.processingState]);

  useEffect(() => {
    const poll = async () => {
      if (document.visibilityState !== 'visible') return;
      const { listWorkspaceRevisions } = await import('./api');
      const live = await listWorkspaceRevisions(options.transactionIds).catch(() => null);
      if (!live) return;
      const liveKey = live.map((row) => `${row.transactionId}:${row.factsRevision}`).join('|');
      if (options.revisions?.length) {
        if (shouldRefetchFromRevisions(options.revisions, live)) void load();
        lastLive.current = liveKey;
        return;
      }
      if (lastLive.current && lastLive.current !== liveKey) void load();
      lastLive.current = liveKey;
    };
    const timer = window.setInterval(() => { void poll(); }, REVISION_POLL_MS);
    return () => window.clearInterval(timer);
  }, [load, revisionsKey, idsKey, options.revisions, options.transactionIds]);
}
